import React, { useRef, useState } from "react";
import { createPortal } from "react-dom";
import { SITE_DISPLAY_STANDARD, SITE_DISPLAY_RRP, SITE_DISPLAY_COMPLIMENTARY } from "@config/config";
import useViewport from "@hooks/useViewport.js";
import VoucherButtons from "@components/VoucherManager/VoucherButtons";
import VoucherLogo from "@components/VoucherManager/VoucherPurchases/VoucherLogo/index.js";

const VoucherItem = ({
                         item,
                         index,
                         selectedItems,
                         setQuantity,
                         idKey,
                         isGiftingMode = false,
                         displayPrice = false,
                         cashbackFlag = false,
                     }) => {
    const {
        key,
    } = item;

    const infoRef = useRef(null);
    const { width } = useViewport();
    const breakpoint = 1219;
    const [tooltipStyle, setTooltipStyle] = useState({});
    const [showTooltip, setShowTooltip] = useState(false);

    const intro = item.short_description;
    const cinemaGroupName = item.cinema_group_name;

    const rrp = item.rrp_price_cents ?? 0;
    const price = item.price_cents ?? 0;
    const saving = rrp - (displayPrice === SITE_DISPLAY_COMPLIMENTARY ? item.display_price_cents : price);

    const cashback = (!isGiftingMode && cashbackFlag && item.cashback_percent != null)
        ? item.cashback_percent
        : null;

    const cashbackSanitised = cashback != null ? String(cashback).slice(0, 4) : null;

    const selected = selectedItems.find((selectedItem) => selectedItem.key === idKey);
    const quantity = selected ? selected.quantity : 0;
    const itemTotal = quantity === 0 ? 0 : ((displayPrice === SITE_DISPLAY_COMPLIMENTARY ? item.display_price_cents : price)) * quantity;

    const handleMouseEnter = () => {
        const rect = infoRef.current.getBoundingClientRect();
        const tooltipWidth = 200;
        const viewportWidth = window.innerWidth;
        const isDesktop = width > breakpoint;

        if (isDesktop) {
            const centredLeft = rect.left + rect.width / 2;
            const wouldOverflow = centredLeft + tooltipWidth / 2 > viewportWidth;

            if (wouldOverflow) {
                // Fall back to mobile style: position to the left
                setTooltipStyle({
                    position: "fixed",
                    top: `${rect.top + rect.height / 2}px`,
                    left: `${rect.left}px`,
                    transform: "translate(-100%, -50%)",
                    isMobileStyle: true,
                });
            } else {
                setTooltipStyle({
                    position: "fixed",
                    top: `${rect.top - 8}px`,
                    left: `${centredLeft}px`,
                    transform: "translate(-50%, -100%)",
                    isMobileStyle: false,
                });
            }
        } else {
            setTooltipStyle({
                position: "fixed",
                top: `${rect.top + rect.height / 2}px`,
                left: `${rect.left}px`,
                transform: "translate(-100%, -50%)",
                isMobileStyle: true,
            });
        }
        setShowTooltip(true);
    };

    const handleMouseLeave = () => setShowTooltip(false);

    return (
        <>
            <div className="voucher__item" key={`voucher-${key}-${index}`}>
                <VoucherLogo item={item} />

                <div className="voucher__content">
                    <div className="voucher__group-name">{cinemaGroupName}</div>
                    <div className="voucher__details">
                        <div className="voucher__terms">
                            <div className="voucher__description">
                                {intro}
                            </div>

                            <div
                                className="voucher__info"
                                ref={infoRef}
                                onMouseEnter={handleMouseEnter}
                                onMouseLeave={handleMouseLeave}
                            >
                                i
                                {showTooltip && createPortal(
                                    <span
                                        className={`voucher__tooltip--portal ${tooltipStyle.isMobileStyle ? "voucher__tooltip--portal-left" : ""}`}
                                        style={tooltipStyle}
                                    >
                                        {item.description || ""}
                                    </span>,
                                document.body
                            )}
                            </div>
                        </div>
                    </div>

                    {displayPrice === SITE_DISPLAY_STANDARD && (
                        <div className="voucher__group">
                            <div className="voucher__pricing">
                                {rrp > 0 && (
                                    <div className="voucher__rrp">RRP ${(rrp / 100).toFixed(2)}</div>
                                )}
                                {saving != null && saving > 0 && (
                                    <div className="voucher__saving">
                                        Save ${(saving / 100).toFixed(2)}
                                    </div>
                                )}
                            </div>
                            <div className="voucher__pricing">
                                <div className="voucher__price">${(price / 100).toFixed(2)}</div>
                            </div>
                        </div>
                    )}

                    {displayPrice === SITE_DISPLAY_RRP && (
                        <div className="voucher__group">
                            <div className="voucher__price">${(rrp / 100).toFixed(2)}</div>
                            {cashbackFlag && cashbackSanitised != null && (
                                <div className="voucher__cashback">{cashbackSanitised}% Cashback</div>
                            )}
                        </div>
                    )}

                    {displayPrice === SITE_DISPLAY_COMPLIMENTARY && (
                        <div className="voucher__group">
                            <div className="voucher__pricing">
                                {rrp > 0 && (
                                    <div className="voucher__rrp">RRP ${(rrp / 100).toFixed(2)}</div>
                                )}
                                {saving != null && saving > 0 && (
                                    <div className="voucher__saving">
                                        Save ${(saving / 100).toFixed(2)}
                                    </div>
                                )}
                            </div>
                            <div className="voucher__pricing">
                                <div className="voucher__price">${(item.display_price_cents / 100).toFixed(2)}</div>
                            </div>
                        </div>
                    )}
                </div>

                <div
                    key={`voucher-${idKey}`}
                    className={`voucher__quantity voucher__quantity--${index}`}
                    data-idkey={idKey}
                >
                    <VoucherButtons
                        value={quantity}
                        setValue={(newQuantity) => {
                            setQuantity(selectedItems, idKey, newQuantity);
                        }}
                    />
                    <div className="voucher__totals">${(itemTotal / 100).toFixed(2)}</div>
                </div>
            </div>
        </>
    );
};

export default VoucherItem;
